import React from 'react';
import './MyApproach.css';

const MyApproach = () => {
  return (
    <div className="my-approach">
      <div className="approach-container">
        <div className="approach-header">
          <h1>My Approach</h1>
          <div className="quote-section">
            <blockquote>
              "All behavior makes sense in context." ~John Bowlby
            </blockquote>
          </div>
        </div>
        
        <div className="approach-content">
          <div className="content-section">
            <p>
              In attachment-based therapy, the therapeutic relationship itself is a key part of the healing process. 
              By forming a secure attachment with the therapist, clients can begin to experience the kind of emotional 
              support and connection they may have lacked in early life. I prioritize creating a safe, non-judgmental 
              space where my client feels seen, heard, and valued.
            </p>
          </div>

          <div className="content-section">
            <p>
              A major goal is to heal attachment wounds. Attachment injuries often stem from early experiences of 
              neglect, emotional abuse, or inconsistent caregiving, leaving clients with unresolved emotional pain 
              and trust issues.
            </p>
          </div>

          <div className="content-section">
            <h2>How do we heal these wounds?</h2>
            <p>
              There are two main approaches in mental health counseling that focus on how to address and intervene 
              in psychological issues. These approaches differ mainly in the order and direction of processing in 
              terms of emotional regulation, behavior, and cognitive changes.
            </p>
          </div>

          <div className="approaches-grid">
            <div className="approach-card top-down">
              <h3>Top-Down Approaches</h3>
              <p>
                The top-down approach is cognitive-focused, often starting with higher-order mental processes 
                (thoughts, beliefs, and cognitions) and working down to emotions and behaviors. In this model, 
                the therapist helps the client identify, challenge, change, or make space for thoughts and beliefs, 
                which then leads to emotional and behavioral changes.
              </p>
              <div className="examples">
                <h4>Examples include:</h4>
                <ul>
                  <li>Cognitive Behavioral Therapy (CBT)</li>
                  <li>Acceptance and Commitment Therapy (ACT)</li>
                  <li>Dialectical Behavior Therapy (DBT)</li>
                </ul>
              </div>
            </div>

            <div className="approach-card bottom-up">
              <h3>Bottom-Up Approaches</h3>
              <p>
                The bottom-up approach, on the other hand, emphasizes starting with the body, emotions, and 
                sensory experiences, and moving upward toward cognitive and mental processing. It is based on 
                the idea that healing starts at the emotional and bodily level, often by addressing the body's 
                response to stress, trauma, and emotional dysregulation, which then leads to cognitive shifts.
              </p>
              <div className="examples">
                <h4>Examples include:</h4>
                <ul>
                  <li>Somatic Experiencing</li>
                  <li>Internal Family Systems (IFS)</li>
                  <li>Emotion Focused Therapy (EFT)</li>
                  <li>Compassionate Inquiry</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="content-section">
            <h2>Which type of approach do you do?</h2>
            <p>
              I am well trained in both approaches. After our intake session, we will create a therapeutic plan 
              and discuss which approach may be best for you. We may also use both approaches during the course 
              of our therapeutic work together. With some clients we may have start with ACT and DBT for emotion 
              regulation and interpersonal skills and then move on to exploring deeper wounds and trauma with IFS and EFT.
            </p>
          </div>

          <div className="content-section">
            <h2>How do I know which approach is right for me?</h2>
            <p>
              The best therapeutic approach often depends on the client's needs, their history, and the nature 
              of the issues they are facing. In many cases, a combination of both approaches leads to a more 
              holistic and effective form of therapy.
            </p>
          </div>

          <div className="content-section grief-note">
            <h2>What about your approach to grief?</h2>
            <p>
              It doesn't seem to fit neatly into either approach. Please click on the tab "Grief" to learn 
              more about healing grief.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyApproach;
